import { useState, useRef, useEffect } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, ChevronLeft, ChevronRight } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';

const PHOTO_ALBUM_URL = process.env.REACT_APP_PHOTO_ALBUM_URL || '';
const INVITATION_URL = process.env.REACT_APP_PHOTO_ALBUM_URL || (typeof window !== 'undefined' ? window.location.origin : '');

function CustomPrevArrow(props: any) {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      type="button"
      className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center border transition-colors hover:bg-[var(--pale-lime)]/40"
      style={{ backgroundColor: 'var(--butter)', borderColor: 'var(--chartreuse)' }}
    >
      <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" style={{ color: 'var(--emerald)' }} />
    </button>
  );
}

function CustomNextArrow(props: any) {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      type="button"
      className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center border transition-colors hover:bg-[var(--pale-lime)]/40"
      style={{ backgroundColor: 'var(--butter)', borderColor: 'var(--chartreuse)' }}
    >
      <ChevronRight className="w-5 h-5 md:w-6 md:h-6" style={{ color: 'var(--emerald)' }} />
    </button>
  );
}

export function PhotoGallery() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const enterStart = vh * 0.7;
      const enterEnd = -vh * 0.15;
      const progress = Math.max(0, Math.min(1, (enterStart - rect.top) / (enterStart - enterEnd)));
      setScrollProgress(progress);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const baseUrl = process.env.PUBLIC_URL || '';
  const galleryExt = 'webp';
  const galleryCount = 10;
  const photos = Array.from({ length: galleryCount }, (_, i) => ({
    url: `${baseUrl}/gallery/${i + 1}.${galleryExt}`,
    alt: `Momento ${i + 1}`,
  }));

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: false,
    centerPadding: '0',
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 1, centerMode: false, centerPadding: '0' } },
      { breakpoint: 640, settings: { slidesToShow: 1, centerMode: false, centerPadding: '0' } },
    ],
  };

  const shareProgress = Math.max(0, Math.min(1, scrollProgress / 0.4));
  const headerProgress = Math.max(0, Math.min(1, (scrollProgress - 0.25) / 0.35));
  const sliderProgress = Math.max(0, Math.min(1, (scrollProgress - 0.45) / 0.35));

  return (
    <section ref={sectionRef} className="relative py-24 px-2 sm:px-4 md:px-6" style={{ backgroundColor: 'var(--butter)' }}>
      <div className="max-w-6xl mx-auto w-full">
        <div
          className="max-w-2xl mx-auto mb-14 transition-all duration-700 ease-out"
          style={{
            opacity: shareProgress,
            transform: `translateY(${28 * (1 - shareProgress)}px)`,
          }}
        >
          <div
            className="rounded-xl p-8 md:p-10 text-center border"
            style={{
              backgroundColor: 'var(--butter)',
              borderColor: 'rgba(186, 159, 56, 0.3)',
            }}
          >
            <QrCode className="w-14 h-14 mx-auto mb-6" style={{ color: 'var(--chartreuse)' }} />
            <h3 className="text-editorial-title text-xl md:text-2xl tracking-[0.2em] mb-4">
              COMPARTE TUS FOTOS
            </h3>
            <p
              className="mb-8 leading-relaxed"
              style={{ color: 'var(--emerald)', opacity: 0.85 }}
            >
              Captura los mejores momentos de nuestra boda y compártelos con nosotros. Escanea el código QR para subir tus fotos al álbum compartido.
            </p>
            <div className="flex flex-col items-center">
              <div
                className="inline-block p-6 rounded-xl border"
                style={{
                  backgroundColor: 'var(--pale-lime)',
                  borderColor: 'var(--chartreuse)',
                }}
              >
                <div
                  className="w-40 h-40 rounded-lg flex items-center justify-center overflow-hidden"
                  style={{ backgroundColor: 'var(--butter)' }}
                >
                  {PHOTO_ALBUM_URL ? (
                    <QRCodeSVG
                      value={PHOTO_ALBUM_URL}
                      size={160}
                      level="M"
                      bgColor="#FCEBD0"
                      fgColor="#1D3B28"
                      className="w-full h-full p-2"
                    />
                  ) : (
                    <div className="text-center" style={{ color: 'var(--emerald)' }}>
                      <QrCode className="w-16 h-16 mx-auto mb-1 opacity-60" />
                      <p className="text-xs opacity-80">Configura REACT_APP_PHOTO_ALBUM_URL</p>
                    </div>
                  )}
                </div>
              </div>
              {INVITATION_URL && (
                <a
                  href={INVITATION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 block text-editorial-meta text-xs tracking-widest underline underline-offset-2 transition-opacity hover:opacity-100"
                  style={{ color: 'var(--emerald)', opacity: 0.85 }}
                >
                  Abrir invitación
                </a>
              )}
            </div>
            <p className="text-editorial-meta text-xs mt-6" style={{ opacity: 0.8 }}>
              El álbum estará disponible el día del evento
            </p>
          </div>
        </div>

        <div
          className="text-center mb-14 transition-all duration-700 ease-out"
          style={{
            opacity: headerProgress,
            transform: `translateY(${24 * (1 - headerProgress)}px)`,
          }}
        >
          <h2 className="text-editorial-title text-2xl md:text-3xl tracking-[0.3em] mb-4">
            NUESTRA MAREA
          </h2>
          <p
            className="text-lg max-w-2xl mx-auto mb-4"
            style={{ color: 'var(--emerald)', opacity: 0.85, fontFamily: 'var(--font-serif)' }}
          >
            Instantes del gran paso que nos trajo hoy a la orilla del mar.
          </p>
          <div className="w-20 h-px mx-auto" style={{ backgroundColor: 'var(--chartreuse)' }} />
        </div>

        <div
          className="mb-14 transition-all duration-700 ease-out"
          style={{
            opacity: sliderProgress,
            transform: `translateY(${28 * (1 - sliderProgress)}px)`,
          }}
        >
          <Slider {...settings} className="wedding-slider">
            {photos.map((photo, index) => (
              <div key={index} className="px-1 sm:px-2 md:px-3 focus:outline-none">
                <div
                  className="rounded-lg overflow-hidden border aspect-[3/4] min-h-[65vmin] sm:min-h-0 w-full"
                  style={{ borderColor: 'rgba(186, 159, 56, 0.25)' }}
                >
                  <ImageWithFallback
                    src={photo.url}
                    alt={photo.alt}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-500"
                    fallbackText={photo.alt}
                  />
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </div>

      <style>{`
        .wedding-slider .slick-list { overflow: hidden; }
        .wedding-slider .slick-slide { box-sizing: border-box; height: inherit; }
        .wedding-slider .slick-slide > div { height: 100%; }
        .wedding-slider .slick-dots { bottom: -40px; }
        .wedding-slider .slick-dots li button:before {
          font-size: 10px;
          color: var(--chartreuse);
          opacity: 0.4;
        }
        .wedding-slider .slick-dots li.slick-active button:before {
          opacity: 1;
          color: var(--chartreuse);
        }
        @media (max-width: 640px) {
          .wedding-slider .slick-slide { display: flex; justify-content: center; }
          .wedding-slider .slick-list { margin: 0; overflow: hidden; }
        }
      `}</style>
    </section>
  );
}
